import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Award, MapPin, Users, Calendar, Loader2, Bitcoin, Globe2, Plus, ShieldOff } from "lucide-react";
import { toast } from "sonner";

const CURRENCIES = [
  { code: "usd", symbol: "$", name: "US Dollar" },
  { code: "eur", symbol: "€", name: "Euro" },
  { code: "gbp", symbol: "£", name: "British Pound" },
  { code: "inr", symbol: "₹", name: "Indian Rupee" },
  { code: "jpy", symbol: "¥", name: "Japanese Yen" },
  { code: "aud", symbol: "A$", name: "Australian Dollar" },
  { code: "cad", symbol: "C$", name: "Canadian Dollar" },
  { code: "sgd", symbol: "S$", name: "Singapore Dollar" },
  { code: "brl", symbol: "R$", name: "Brazilian Real" },
  { code: "aed", symbol: "د.إ", name: "UAE Dirham" },
  { code: "chf", symbol: "CHF", name: "Swiss Franc" },
  { code: "mxn", symbol: "MX$", name: "Mexican Peso" },
  { code: "zar", symbol: "R", name: "South African Rand" },
  { code: "hkd", symbol: "HK$", name: "Hong Kong Dollar" },
];

export default function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { currency: globalCurrency } = useCurrency();
  const { add: addToCart } = useCart();
  const [p, setP] = useState(null);
  const [tons, setTons] = useState(10);
  const [currency, setCurrency] = useState(globalCurrency || "usd");
  const [crypto, setCrypto] = useState(false);
  const [anonymous, setAnonymous] = useState(false);
  const [rates, setRates] = useState({});
  const [buying, setBuying] = useState(false);

  useEffect(() => { setCurrency(globalCurrency); }, [globalCurrency]);

  useEffect(() => {
    api.get(`/projects/${id}`).then((r) => setP(r.data)).catch(() => nav("/marketplace"));
    api.get("/currencies").then((r) => {
      const m = {};
      (r.data.rates || []).forEach((c) => { m[c.code] = c; });
      setRates(m);
    });
  }, [id]);

  useEffect(() => {
    if (crypto && currency !== "usd") {
      setCurrency("usd");
      toast.info("Crypto payments settle in USD");
    }
  }, [crypto]);

  const buy = async () => {
    if (!user) { nav("/login"); return; }
    if (tons < 1) return toast.error("Enter at least 1 ton");
    setBuying(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        project_id: p.id,
        tons: Number(tons),
        origin_url: window.location.origin,
        currency,
        include_crypto: crypto,
        anonymous,
        country: user.country || null,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setBuying(false);
    }
  };

  const addPortfolio = () => {
    addToCart(p, Number(tons) || 1);
    toast.success(`Added ${tons} tons of ${p.name} to portfolio`);
  };

  if (!p) return <div className="p-12 text-stone-500">Loading…</div>;

  const rate = rates[currency]?.rate_vs_usd || 1;
  const symbol = rates[currency]?.symbol || "$";
  const localPrice = (p.price_per_ton * rate);
  const total = (Number(tons) * localPrice).toFixed(2);
  const usdTotal = (Number(tons) * p.price_per_ton).toFixed(2);

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <div className="aspect-[16/9] rounded-3xl overflow-hidden">
            <img src={p.image_url} alt={p.name} className="w-full h-full object-cover"/>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Badge className="bg-[#2C5530]/10 text-[#2C5530] border-0 capitalize hover:bg-[#2C5530]/10">{p.category.replace("_"," ")}</Badge>
            <span className="text-sm text-stone-500 flex items-center gap-1"><Award className="w-4 h-4"/>{p.certification}</span>
            <span className="text-sm text-stone-500 flex items-center gap-1"><MapPin className="w-4 h-4"/>{p.location}</span>
            {p.year && <span className="text-sm text-stone-500 flex items-center gap-1"><Calendar className="w-4 h-4"/>Vintage {p.year}</span>}
          </div>
          <h1 className="mt-4 text-4xl sm:text-5xl tracking-tighter font-light" data-testid="project-title">{p.name}</h1>
          <p className="mt-6 text-lg text-stone-600 leading-relaxed max-w-3xl">{p.description}</p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-10 pt-8 border-t border-stone-200">
            <div>
              <div className="text-xs uppercase tracking-widest text-stone-500">Available</div>
              <div className="text-2xl font-light mt-1">{p.available_tons.toLocaleString()} t</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-stone-500">Developer</div>
              <div className="text-base mt-1">{p.project_developer || "—"}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-stone-500">Certification</div>
              <div className="text-base mt-1">{p.certification}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-stone-500">Ships to</div>
              <div className="text-base mt-1 flex items-center gap-1"><Globe2 className="w-4 h-4 text-[#2C5530]"/> Any country</div>
            </div>
          </div>
        </div>

        <aside className="lg:sticky lg:top-24 h-fit">
          <div className="border border-stone-200 rounded-2xl p-6 bg-white">
            <div className="flex items-baseline justify-between">
              <div>
                <div className="text-3xl font-light tracking-tight">{symbol}{localPrice.toFixed(2)}</div>
                <div className="text-xs uppercase tracking-widest text-stone-500 mt-1">per ton CO₂</div>
              </div>
              <Users className="w-5 h-5 text-stone-400"/>
            </div>

            <div className="mt-6">
              <Label htmlFor="tons">Tons to purchase</Label>
              <Input
                id="tons"
                data-testid="tons-input"
                type="number"
                min={1}
                max={p.available_tons}
                value={tons}
                onChange={(e) => setTons(e.target.value)}
                className="mt-2 h-12 bg-stone-50 border-stone-200"
              />
              <div className="flex flex-wrap gap-2 mt-3">
                {[10, 50, 100, 500].map((n) => (
                  <button key={n} onClick={() => setTons(n)} className="px-3 py-1 rounded-full border border-stone-200 text-xs hover:border-[#2C5530]" data-testid={`quick-${n}`}>{n}t</button>
                ))}
              </div>
            </div>

            <div className="mt-5">
              <Label>Pay in</Label>
              <Select value={currency} onValueChange={setCurrency} disabled={crypto}>
                <SelectTrigger data-testid="currency-select" className="mt-2 h-11 bg-stone-50 border-stone-200">
                  <SelectValue/>
                </SelectTrigger>
                <SelectContent className="bg-white max-h-64">
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.symbol} · {c.name} ({c.code.toUpperCase()})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {crypto && <div className="text-xs text-stone-500 mt-1.5">Crypto settles in USD</div>}
            </div>

            <div className="mt-5 flex items-center justify-between p-3 rounded-xl bg-stone-50 border border-stone-200">
              <div className="flex items-center gap-2">
                <Bitcoin className="w-5 h-5 text-[#D97757]"/>
                <div>
                  <div className="text-sm font-medium">Pay with crypto</div>
                  <div className="text-xs text-stone-500">BTC · ETH · USDC via Stripe</div>
                </div>
              </div>
              <Switch checked={crypto} onCheckedChange={setCrypto} data-testid="crypto-toggle"/>
            </div>

            <div className="mt-3 flex items-center justify-between p-3 rounded-xl bg-stone-50 border border-stone-200">
              <div className="flex items-center gap-2">
                <ShieldOff className="w-5 h-5 text-stone-600"/>
                <div>
                  <div className="text-sm font-medium">Retire anonymously</div>
                  <div className="text-xs text-stone-500">Hide company on public certificate</div>
                </div>
              </div>
              <Switch checked={anonymous} onCheckedChange={setAnonymous} data-testid="anonymous-toggle"/>
            </div>

            <div className="mt-6 pt-6 border-t border-stone-200 flex items-baseline justify-between">
              <div>
                <div className="text-sm text-stone-500">Total</div>
                {currency !== "usd" && !crypto && (
                  <div className="text-xs text-stone-400 mt-0.5">≈ ${usdTotal} USD</div>
                )}
              </div>
              <div className="text-3xl font-light tracking-tight text-[#2C5530]" data-testid="total-amount">{symbol}{total}</div>
            </div>

            <Button
              onClick={buy}
              disabled={buying}
              data-testid="buy-btn"
              className="w-full mt-6 h-12 bg-[#2C5530] hover:bg-[#1F3D22] text-white rounded-full text-base"
            >
              {buying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Redirecting…</> : (user ? "Buy credits" : "Sign in to buy")}
            </Button>
            <Button
              variant="outline"
              onClick={addPortfolio}
              data-testid="add-portfolio-btn"
              className="w-full mt-2 h-11 rounded-full border-stone-300"
            >
              <Plus className="w-4 h-4 mr-1"/> Add to portfolio
            </Button>
            <div className="text-xs text-stone-500 mt-3 text-center flex items-center justify-center gap-1">
              <Globe2 className="w-3 h-3"/> 14 currencies · card, crypto, wallets · powered by Stripe
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
